import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Bell, CheckCircle2, AlertTriangle, Clock, FileText, Calendar, Info, Moon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIF_CONFIG = {
  doc_expiring: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-400/10' },
  shift_reminder: { icon: Calendar, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  reward: { icon: CheckCircle2, color: 'text-[#A8E6CF]', bg: 'bg-[#A8E6CF]/10' },
  system: { icon: Info, color: 'text-[#00A6A0]', bg: 'bg-[#00A6A0]/10' },
  default: { icon: Bell, color: 'text-muted-foreground', bg: 'bg-muted' },
};

export default function CaregiverNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [quietHours, setQuietHours] = useState(null);
  const [myOverride, setMyOverride] = useState({ has_override: false, opt_out: false, custom_start: '', custom_end: '' });
  const [savingOverride, setSavingOverride] = useState(false);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/caregiver-management/notifications`, { withCredentials: true });
      setNotifications(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuietHours = async () => {
    try {
      const [orgRes, overrideRes] = await Promise.all([
        axios.get(`${API_URL}/api/org/quiet-hours`, { withCredentials: true }),
        axios.get(`${API_URL}/api/org/quiet-hours/my-override`, { withCredentials: true }),
      ]);
      if (orgRes.data.enabled) setQuietHours(orgRes.data);
      if (overrideRes.data) setMyOverride(overrideRes.data);
    } catch { /* ignore */ }
  };

  const saveOverride = async () => {
    setSavingOverride(true);
    try {
      await axios.put(`${API_URL}/api/org/quiet-hours/my-override`, {
        opt_out: myOverride.opt_out,
        custom_start: myOverride.custom_start || null,
        custom_end: myOverride.custom_end || null,
      }, { withCredentials: true });
      toast.success('Quiet hours preference saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSavingOverride(false);
  };

  useEffect(() => { fetchNotifications(); fetchQuietHours(); }, []);

  const markRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/caregiver-management/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (err) {
      console.error(err);
    }
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read && !n.id.startsWith('exp_') && !n.id.startsWith('shift_'));
    for (const n of unread) {
      await markRead(n.id);
    }
    toast.success('All marked as read');
  };

  const filtered = filter === 'all' ? notifications
    : filter === 'unread' ? notifications.filter(n => !n.read)
    : notifications.filter(n => n.type === filter);

  const unreadCount = notifications.filter(n => !n.read).length;

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="caregiver-notifications-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
              Notifications
              {unreadCount > 0 && (
                <span className="text-sm px-2 py-0.5 rounded-full bg-[#FF6F61]/10 text-[#FF6F61]" data-testid="unread-count">
                  {unreadCount}
                </span>
              )}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Shift reminders, document alerts, and updates</p>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" className="border-border text-foreground hover:bg-muted"
              onClick={markAllRead} data-testid="mark-all-read-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark All Read
            </Button>
          )}
        </div>

        {/* Quiet Hours Info & Personal Override */}
        {quietHours && (
          <Card className="glass-card border-[#8B5CF6]/20" data-testid="quiet-hours-info">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start gap-3">
                <Moon className="w-4 h-4 text-[#8B5CF6] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-foreground font-medium">Quiet Hours: {quietHours.start} – {quietHours.end}</p>
                  <p className="text-xs text-muted-foreground">Non-urgent notifications are paused. Emergency alerts always come through.</p>
                </div>
              </div>
              <div className="border-t border-border/40 pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-foreground font-medium">Opt out of quiet hours</p>
                    <p className="text-[10px] text-muted-foreground">Receive all notifications even during quiet hours (e.g. night shift)</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-testid="opt-out-toggle">
                    <input type="checkbox" checked={myOverride.opt_out}
                      onChange={e => setMyOverride(o => ({ ...o, opt_out: e.target.checked }))}
                      className="sr-only peer" />
                    <div className="w-9 h-5 bg-muted peer-checked:bg-[#8B5CF6] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4" />
                  </label>
                </div>
                {!myOverride.opt_out && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-muted-foreground">Custom window:</span>
                    <input type="time" value={myOverride.custom_start || ''}
                      onChange={e => setMyOverride(o => ({ ...o, custom_start: e.target.value }))}
                      placeholder="Start"
                      className="rounded-md border border-border bg-muted px-2 py-1 text-xs text-foreground w-24"
                      data-testid="override-start" />
                    <span className="text-[10px] text-muted-foreground">to</span>
                    <input type="time" value={myOverride.custom_end || ''}
                      onChange={e => setMyOverride(o => ({ ...o, custom_end: e.target.value }))}
                      placeholder="End"
                      className="rounded-md border border-border bg-muted px-2 py-1 text-xs text-foreground w-24"
                      data-testid="override-end" />
                  </div>
                )}
                <Button size="sm" className="btn-primary text-xs" onClick={saveOverride} disabled={savingOverride} data-testid="save-override-btn">
                  {savingOverride ? 'Saving...' : 'Save Preference'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'all', label: 'All' },
            { key: 'unread', label: 'Unread' },
            { key: 'doc_expiring', label: 'Documents' },
            { key: 'shift_reminder', label: 'Shifts' },
          ].map(f => (
            <Button key={f.key} variant={filter === f.key ? 'default' : 'outline'} size="sm"
              onClick={() => setFilter(f.key)}
              className={filter === f.key ? 'btn-primary' : 'border-border text-foreground hover:bg-muted'}
              data-testid={`filter-${f.key}`}>
              {f.label}
            </Button>
          ))}
        </div>

        {/* Notifications List */}
        <Card className="glass-card border-border">
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filtered.map(notif => {
                  const cfg = NOTIF_CONFIG[notif.type] || NOTIF_CONFIG.default;
                  const Icon = cfg.icon;
                  return (
                    <div key={notif.id}
                      className={`p-4 flex items-start gap-4 hover:bg-muted/30 transition-colors cursor-pointer ${
                        !notif.read ? 'border-l-2 border-l-[#FF6F61]' : ''
                      }`}
                      onClick={() => !notif.read && markRead(notif.id)}
                      data-testid={`notif-${notif.id}`}
                    >
                      <div className={`p-2 rounded-lg ${cfg.bg} flex-shrink-0 mt-0.5`}>
                        <Icon className={`w-4 h-4 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${!notif.read ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                            {notif.title}
                          </p>
                          <span className="text-[10px] text-muted-foreground flex-shrink-0">
                            {timeAgo(notif.created_at)}
                          </span>
                        </div>
                        {notif.message && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{notif.message}</p>
                        )}
                        {notif.priority === 'warning' && (
                          <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-400">
                            Action Required
                          </span>
                        )}
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 rounded-full bg-[#FF6F61] flex-shrink-0 mt-2" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
